import React, { useState } from 'react';
import {
  Button,
  Card,
  Table,
  Tabs,
  Typography,
  Statistic,
  Row,
  Col,
  Progress,
} from 'antd';
import useMemoryMonitor from '@/utils/hooks/useMemoryMonitor';
import './MemoryDashboard.scss';

const { Text } = Typography;
const { TabPane } = Tabs;

interface MemoryDashboardProps {
  visible: boolean;
  onClose: () => void;
}

const MemoryDashboard: React.FC<MemoryDashboardProps> = ({
  visible,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const {
    memoryInfo,
    stats,
    isActive,
    isSupported,
    startMonitoring,
    stopMonitoring,
    takeSample,
    clearSamples,
    exportData,
    getSamples,
  } = useMemoryMonitor({
    interval: 2000,
    maxSamples: 200,
  });

  // Check if we should show the dashboard based on URL parameter or environment
  const shouldShowDashboard = () => {
    // Always show in development mode
    if (process.env.NODE_ENV === 'development') return visible;

    // In production, check for URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    return visible && urlParams.has('showMemoryMonitor');
  };

  if (!shouldShowDashboard()) {
    return null;
  }

  if (!isSupported) {
    return (
      <div className='memory-dashboard'>
        <Card title='Memory Monitoring Dashboard'>
          <Text type='danger'>
            Memory monitoring is not supported in this browser. Only
            Chrome/Chromium browsers support the performance.memory API.
          </Text>
          <div className='dashboard-actions'>
            <Button onClick={onClose}>Close</Button>
          </div>
        </Card>
      </div>
    );
  }

  const formatBytes = (bytes: number | undefined) => {
    if (bytes === undefined) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const formatPercentage = (value: number | undefined) => {
    if (value === undefined) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  const handleExportData = () => {
    const dataStr = exportData();
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;

    const exportFileDefaultName = `memory-data-${new Date().toISOString()}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const columns = [
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp: number) => new Date(timestamp).toLocaleTimeString(),
    },
    {
      title: 'Used Heap',
      dataIndex: 'usedJSHeapSize',
      key: 'usedJSHeapSize',
      render: (bytes: number) => formatBytes(bytes),
    },
    {
      title: 'Total Heap',
      dataIndex: 'totalJSHeapSize',
      key: 'totalJSHeapSize',
      render: (bytes: number) => formatBytes(bytes),
    },
    {
      title: 'Heap Limit',
      dataIndex: 'jsHeapSizeLimit',
      key: 'jsHeapSizeLimit',
      render: (bytes: number) => formatBytes(bytes),
    },
    {
      title: 'Usage %',
      dataIndex: 'usagePercentage',
      key: 'usagePercentage',
      render: (percentage: number) => formatPercentage(percentage),
    },
  ];

  return (
    <div className='memory-dashboard'>
      <Card
        title='Memory Monitoring Dashboard'
        extra={<Button onClick={onClose}>Close</Button>}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab='Overview' key='overview'>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title='Current Memory Usage'
                  value={memoryInfo?.usagePercentage || 0}
                  precision={2}
                  suffix='%'
                />
                <Progress
                  percent={memoryInfo?.usagePercentage || 0}
                  status={
                    memoryInfo?.usagePercentage &&
                    memoryInfo.usagePercentage > 90
                      ? 'exception'
                      : 'normal'
                  }
                  strokeColor={
                    memoryInfo?.usagePercentage &&
                    memoryInfo.usagePercentage > 70 &&
                    memoryInfo.usagePercentage <= 90
                      ? '#ff9800' // warning color
                      : undefined
                  }
                  strokeWidth={10}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title='Used Memory'
                  value={formatBytes(memoryInfo?.usedJSHeapSize)}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title='Total Allocated'
                  value={formatBytes(memoryInfo?.totalJSHeapSize)}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title='Memory Limit'
                  value={formatBytes(memoryInfo?.jsHeapSizeLimit)}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: '20px' }}>
              <Col span={6}>
                <Statistic
                  title='Min Usage'
                  value={formatPercentage(stats?.min)}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title='Max Usage'
                  value={formatPercentage(stats?.max)}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title='Average Usage'
                  value={formatPercentage(stats?.avg)}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title='Samples Collected'
                  value={stats?.sampleCount || 0}
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
            </Row>
          </TabPane>

          <TabPane tab='Data' key='data'>
            <Table
              dataSource={getSamples()}
              columns={columns}
              rowKey='timestamp'
              pagination={{ pageSize: 10 }}
              scroll={{ y: 300 }}
            />
          </TabPane>
        </Tabs>

        <div className='dashboard-actions'>
          {isActive ? (
            <Button type='primary' danger onClick={stopMonitoring}>
              Stop Monitoring
            </Button>
          ) : (
            <Button type='primary' onClick={startMonitoring}>
              Start Monitoring
            </Button>
          )}
          <Button onClick={takeSample}>Take Sample</Button>
          <Button onClick={clearSamples}>Clear Samples</Button>
          <Button type='primary' onClick={handleExportData}>
            Export Data
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default MemoryDashboard;
